// Home: the phone's home screens. Pages of widgets and app icons on a four-
// column grid, swiped sideways, with Prime one tap away. Tap opens; hold an
// app for its quick actions; hold the background (or pick Edit Home Screen)
// to arrange: things wiggle, drag to reorder, tap for size and page, the minus
// takes them off, the plus adds widgets and apps. The arrangement is synced on
// the Settings record (os/layout.ts), and while arranging a local draft moves
// under the finger so a drag writes once, on drop.
import { useAccess } from '@/state/access'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Check, Minus, Plus } from 'lucide-react'
import { useAllLists } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { sortLists } from '@/kinds/list/components'
import type { NavItem } from '@/lib/nav'
import { Sheet, cx } from '@/components/ui'
import { Backdrop } from '@/os/Backdrop'
import {
  addItem,
  addPage,
  dropEmptyPages,
  MAX_PAGES,
  moveItem,
  moveToPage,
  newKey,
  onHome,
  removeItem,
  resizeItem,
  span,
  WIDGET_KINDS,
  adminWidgets,
  homeRow,
  WIDGET_SIZES,
  type HomeItem,
  type PhoneLayout,
  type WidgetKind,
  type WidgetSize,
} from '@/os/layout'
import { savePhone, useEditing, usePhoneApps, usePhoneLayout } from '@/os/phone'
import { AppLabel, AppTile, useHold } from '@/os/AppIcon'
import { AppMenu } from '@/os/AppMenu'
import { AskPrimeBar, AskPrimeSheet } from '@/os/AskPrime'
import { Widget, WIDGETS } from '@/os/widgets'

const SIZE_LABEL: Record<WidgetSize, string> = { small: 'Small', wide: 'Wide', large: 'Large' }
const DRAG_SLOP = 6

interface Drag {
  key: string
  page: number
  pointer: number
  startX: number
  startY: number
  x: number
  y: number
  dx: number
  dy: number
  w: number
  h: number
  moving: boolean
}

export const HomeView = () => {
  const saved = usePhoneLayout()
  const { byId } = usePhoneApps()
  const navigate = useNavigate()
  const editing = useEditing((s) => s.home)
  const setEditing = useEditing((s) => s.set)
  const [draft, setDraft] = useState<PhoneLayout | null>(null)
  const layout = editing && draft ? draft : saved
  const [page, setPage] = useState(0)
  const [menuApp, setMenuApp] = useState<NavItem | null>(null)
  const [itemSheet, setItemSheet] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [asking, setAsking] = useState(false)
  const [drag, setDrag] = useState<Drag | null>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const now = useNow(60_000)

  /** every change while arranging: shown at once, written through */
  const commit = (next: PhoneLayout) => {
    setDraft(next)
    void savePhone(next)
  }
  const startEditing = () => {
    setDraft(saved)
    setEditing({ home: true })
  }
  const done = () => {
    const tidy = dropEmptyPages(layout)
    if (tidy.pages.length !== layout.pages.length) void savePhone(tidy)
    setDraft(null)
    setEditing({ home: false })
    setPage((p) => Math.min(p, tidy.pages.length - 1))
  }
  // Home is exactly one screen: start it at the top before the first paint, so an
  // offset left over from a long page (Today, scrolled) cannot shift it for a frame
  useLayoutEffect(() => {
    if (window.scrollY) window.scrollTo(0, 0)
  }, [])
  // and on its first page, like a phone's home screen: a reload would otherwise
  // have the browser put the pager back where it was (it restores inner scrollers too)
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    el.scrollLeft = 0
    const again = requestAnimationFrame(() => {
      el.scrollLeft = 0
    })
    return () => cancelAnimationFrame(again)
  }, [])
  // and it stays one screen: nothing underneath scrolls or bounces while Home is open
  useEffect(() => {
    document.documentElement.classList.add('home-still')
    return () => document.documentElement.classList.remove('home-still')
  }, [])
  // edit mode can be entered from a held app's menu, which never saw a draft
  if (editing && !draft) setDraft(saved)

  const goToPage = (i: number) => {
    const el = scroller.current
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }

  // ---- dragging, in edit mode only ----
  const onItemDown = (e: React.PointerEvent, it: HomeItem, pageIndex: number) => {
    if (!editing) return
    const el = e.currentTarget as HTMLElement
    const r = el.getBoundingClientRect()
    el.setPointerCapture(e.pointerId)
    setDrag({
      key: it.k,
      page: pageIndex,
      pointer: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      dx: e.clientX - r.left,
      dy: e.clientY - r.top,
      w: r.width,
      h: r.height,
      moving: false,
    })
  }
  const onItemMove = (e: React.PointerEvent) => {
    if (!drag || drag.pointer !== e.pointerId) return
    const moving = drag.moving || Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > DRAG_SLOP
    setDrag({ ...drag, x: e.clientX, y: e.clientY, moving })
    if (!moving || !draft) return
    // the thing under the finger (not the one being carried) takes its place
    const over = document
      .elementsFromPoint(e.clientX, e.clientY)
      .map((n) => (n as HTMLElement).closest<HTMLElement>('[data-home-key]'))
      .find((n) => n && n.dataset.homeKey !== drag.key && Number(n.dataset.homePage) === drag.page)
    if (!over) return
    const items = draft.pages[drag.page] ?? []
    const from = items.findIndex((x) => x.k === drag.key)
    const to = items.findIndex((x) => x.k === over.dataset.homeKey)
    if (from >= 0 && to >= 0 && from !== to) setDraft(moveItem(draft, drag.page, from, to))
  }
  const onItemUp = (e: React.PointerEvent, it: HomeItem) => {
    if (!drag || drag.pointer !== e.pointerId) return
    const wasMoving = drag.moving
    setDrag(null)
    if (wasMoving) {
      if (draft) void savePhone(draft)
    } else setItemSheet(it.k)
  }

  const current = layout.pages.flat().find((x) => x.k === itemSheet) ?? null
  const currentPage = current ? layout.pages.findIndex((p) => p.some((x) => x.k === current.k)) : -1
  const dragged = drag?.moving ? layout.pages.flat().find((x) => x.k === drag.key) : undefined
  // a clock widget on the page in view already says the time
  const clockShown = (layout.pages[page] ?? []).some((x) => x.t === 'widget' && x.kind === 'clock')

  return (
    <div
      // exactly the screen above the tab bar and the player (--above-tabbar counts it while it
      // shows), so Ask Prime and the dots never hide under either; on a desktop there is no tab
      // bar: the inset panel (1rem of margin, its border, main's top padding) less the player
      className="relative -mx-3 -mt-2 flex flex-col h-[calc(100dvh-var(--above-tabbar)-0.75rem-env(safe-area-inset-top))] md:h-[calc(100dvh-1rem-2px-var(--player-h,0px))] select-none hold-safe"
      data-testid="home"
      data-editing={editing || undefined}
    >
      {/* the backdrop fills the screen behind the page, under the tab bar too */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden>
        <Backdrop kind={layout.wallpaper} />
      </div>
      <header className="relative flex items-center justify-between px-4 pt-2 pb-3 min-h-11">
        {/* one line: the day, and the time beside it where nothing else shows one (on a
            phone the status bar's clock is right above, and a clock widget says it too) */}
        <span className="min-w-0 flex items-baseline gap-2.5">
          {!clockShown && (
            <span className="t-title tabular max-md:hidden" data-testid="home-time">
              {new Date(now).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          <span className="text-[15px] text-muted truncate max-md:text-[17px] max-md:font-semibold max-md:text-fg-2">
            {new Date(now).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })}
          </span>
        </span>
        {editing ? (
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAdding(true)}
              aria-label="Add a widget or app"
              className="h-9 w-9 rounded-full bg-raised border border-line flex items-center justify-center"
              data-testid="home-add"
            >
              <Plus size={18} aria-hidden />
            </button>
            <button
              type="button"
              onClick={done}
              className="rounded-full bg-accent text-on-accent px-4 min-h-9 text-sm font-medium"
              data-testid="home-done"
            >
              Done
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            // muted, not faint: it has to read over every wallpaper, the dark ones too
            className="text-[13px] text-muted hover:text-fg min-h-9 px-2"
            data-testid="home-edit"
          >
            Edit
          </button>
        )}
      </header>

      <div
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget
          setPage(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)))
        }}
        className={cx(
          'relative flex-1 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-none',
          drag?.moving && 'overflow-x-hidden',
        )}
      >
        {layout.pages.map((items, pi) => (
          <HomePage
            key={pi}
            index={pi}
            items={items}
            byId={byId}
            layout={layout}
            editing={editing}
            dragKey={drag?.moving ? drag.key : null}
            onOpen={(to) => void navigate({ to })}
            onAppHold={setMenuApp}
            onBackgroundHold={startEditing}
            onWidgetHold={(k) => setItemSheet(k)}
            onItemDown={onItemDown}
            onItemMove={onItemMove}
            onItemUp={onItemUp}
            onRemove={(k) => commit(removeItem(layout, k))}
          />
        ))}
      </div>

      <div className="relative px-4 pt-2 flex flex-col gap-2.5">
        <AskPrimeBar onOpen={() => setAsking(true)} />
        <div className="flex items-center justify-center gap-2 min-h-5" aria-label="Pages">
          {layout.pages.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goToPage(i)}
              aria-label={`Page ${i + 1}`}
              aria-current={i === page || undefined}
              className={cx('h-2 rounded-full transition-all', i === page ? 'w-5 bg-fg' : 'w-2 bg-fg/30')}
            />
          ))}
          {editing && layout.pages.length < MAX_PAGES && (
            <button
              type="button"
              onClick={() => {
                const next = addPage(layout)
                commit(next)
                requestAnimationFrame(() => goToPage(next.pages.length - 1))
              }}
              aria-label="Add a page"
              className="h-5 w-5 rounded-full border border-line text-muted flex items-center justify-center"
              data-testid="home-add-page"
            >
              <Plus size={12} aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* what is being carried follows the finger; its place in the grid stays dimmed */}
      {dragged && drag && (
        <div
          className="fixed z-50 pointer-events-none scale-105 opacity-90"
          style={{ left: drag.x - drag.dx, top: drag.y - drag.dy, width: drag.w, height: drag.h }}
          aria-hidden
        >
          {dragged.t === 'app' ? (
            byId.get(dragged.id) && (
              <div className="flex flex-col items-center gap-1.5">
                <AppTile app={byId.get(dragged.id)!} tint={layout.icons} size={layout.iconSize} />
              </div>
            )
          ) : (
            <div className="h-full w-full rounded-[var(--radius-card)] border border-accent/50 bg-panel/95 p-3 text-[13px] font-semibold text-muted">
              {WIDGETS[dragged.kind].name}
            </div>
          )}
        </div>
      )}

      <AppMenu app={menuApp} layout={layout} from="home" onClose={() => setMenuApp(null)} />
      <AskPrimeSheet open={asking} onClose={() => setAsking(false)} />
      <AddSheet
        open={adding}
        onClose={() => setAdding(false)}
        layout={layout}
        page={Math.min(page, layout.pages.length - 1)}
        byId={byId}
        onAdd={(it) => {
          commit(addItem(layout, it, Math.min(page, layout.pages.length - 1)))
          setAdding(false)
        }}
      />
      <ItemSheet
        item={current}
        page={currentPage}
        layout={layout}
        byId={byId}
        editing={editing}
        onClose={() => setItemSheet(null)}
        onChange={(next) => commit(next)}
        onEdit={startEditing}
      />
    </div>
  )
}

const HomePage = ({
  index,
  items,
  byId,
  layout,
  editing,
  dragKey,
  onOpen,
  onAppHold,
  onBackgroundHold,
  onWidgetHold,
  onItemDown,
  onItemMove,
  onItemUp,
  onRemove,
}: {
  index: number
  items: HomeItem[]
  byId: Map<string, NavItem>
  layout: PhoneLayout
  editing: boolean
  dragKey: string | null
  onOpen: (to: string) => void
  onAppHold: (app: NavItem) => void
  onBackgroundHold: () => void
  onWidgetHold: (key: string) => void
  onItemDown: (e: React.PointerEvent, it: HomeItem, page: number) => void
  onItemMove: (e: React.PointerEvent) => void
  onItemUp: (e: React.PointerEvent, it: HomeItem) => void
  onRemove: (key: string) => void
}) => {
  const bg = useHold(onBackgroundHold, 550)
  // apps that no longer resolve (an unpinned page, a hidden workspace page) are skipped, not shown broken
  const shown = items.filter((it) => it.t === 'widget' || (byId.has(it.id) && !layout.hidden.includes(it.id)))
  // a home page swipes sideways only, like a phone's; one with more on it than
  // fits may still scroll, so nothing is ever out of reach (style writes, not state)
  const pageRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = pageRef.current
    if (!el) return
    const fit = () => {
      const over = el.scrollHeight > el.clientHeight + 1
      el.style.touchAction = over ? 'pan-x pan-y' : 'pan-x'
      el.style.overflowY = over ? 'auto' : 'hidden'
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child)
    return () => ro.disconnect()
  }, [items.length])
  return (
    <div
      ref={pageRef}
      className="w-full shrink-0 snap-center px-4 overflow-y-hidden overscroll-contain"
      {...(editing ? {} : bg.handlers)}
      onPointerDown={(e) => {
        // only the empty background arranges; a held icon has its own menu
        if (!editing && e.target === e.currentTarget) bg.handlers.onPointerDown(e)
      }}
      data-testid={`home-page-${index}`}
    >
      <div
        // while arranging, room above the first row for the widgets' remove badges, which sit out of their corner
        className={cx(
          'grid grid-cols-4 gap-x-3 gap-y-3 grid-flow-row-dense content-start pb-2',
          editing && 'pt-2.5',
        )}
        style={{ gridAutoRows: `${homeRow(layout.iconSize)}px` }}
        onPointerDown={(e) => {
          if (!editing && e.target === e.currentTarget) bg.handlers.onPointerDown(e)
        }}
      >
        {shown.map((it, i) => (
          <HomeCell
            key={it.k}
            it={it}
            index={i}
            page={index}
            app={it.t === 'app' ? byId.get(it.id) : undefined}
            tint={layout.icons}
            iconSize={layout.iconSize}
            editing={editing}
            carried={dragKey === it.k}
            onOpen={onOpen}
            onAppHold={onAppHold}
            onWidgetHold={onWidgetHold}
            onItemDown={onItemDown}
            onItemMove={onItemMove}
            onItemUp={onItemUp}
            onRemove={onRemove}
          />
        ))}
        {shown.length === 0 && (
          <p className="col-span-4 row-span-2 flex items-center justify-center text-sm text-faint text-center px-6">
            {editing
              ? 'An empty page. The plus adds widgets and apps.'
              : 'Nothing here. Hold the background to arrange.'}
          </p>
        )}
      </div>
    </div>
  )
}

const HomeCell = ({
  it,
  index,
  page,
  app,
  tint,
  iconSize,
  editing,
  carried,
  onOpen,
  onAppHold,
  onWidgetHold,
  onItemDown,
  onItemMove,
  onItemUp,
  onRemove,
}: {
  it: HomeItem
  index: number
  page: number
  app?: NavItem
  tint: 'colour' | 'mono'
  iconSize: number
  editing: boolean
  carried: boolean
  onOpen: (to: string) => void
  onAppHold: (app: NavItem) => void
  onWidgetHold: (key: string) => void
  onItemDown: (e: React.PointerEvent, it: HomeItem, page: number) => void
  onItemMove: (e: React.PointerEvent) => void
  onItemUp: (e: React.PointerEvent, it: HomeItem) => void
  onRemove: (key: string) => void
}) => {
  const hold = useHold(() => (app ? onAppHold(app) : onWidgetHold(it.k)))
  const [c, r] = span(it)
  const style = { gridColumn: `span ${c} / span ${c}`, gridRow: `span ${r} / span ${r}` }
  const edit = editing
    ? {
        onPointerDown: (e: React.PointerEvent) => onItemDown(e, it, page),
        onPointerMove: onItemMove,
        onPointerUp: (e: React.PointerEvent) => onItemUp(e, it),
        onPointerCancel: (e: React.PointerEvent) => onItemUp(e, it),
      }
    : it.t === 'widget'
      ? // a widget's own buttons stay live; only a hold on it opens its sheet
        hold.handlers
      : {}
  return (
    <div
      className={cx(
        'relative min-w-0',
        editing && 'os-jiggle touch-none',
        editing && index % 2 === 1 && 'os-jiggle-alt',
        carried && 'opacity-30',
      )}
      style={style}
      data-home-key={it.k}
      data-home-page={page}
      {...edit}
    >
      {it.t === 'app' ? (
        app && (
          <button
            type="button"
            className="w-full h-full flex flex-col items-center justify-start gap-1.5 pt-1"
            onClick={() => {
              if (editing || hold.held()) return
              onOpen(app.to)
            }}
            {...(editing ? {} : hold.handlers)}
            aria-label={app.label}
            data-testid={`home-app-${app.key}`}
          >
            <AppTile app={app} tint={tint} size={iconSize} />
            <AppLabel>{app.label}</AppLabel>
          </button>
        )
      ) : (
        <Widget kind={it.kind} size={it.size} refId={it.ref} editing={editing} />
      )}
      {editing && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            onRemove(it.k)
          }}
          aria-label={`Remove ${app?.label ?? WIDGETS[(it as { kind: WidgetKind }).kind]?.name ?? ''}`}
          className={cx(
            'absolute z-10 h-6 w-6 rounded-full bg-raised border border-line-strong text-fg flex items-center justify-center shadow',
            it.t === 'app' ? 'top-0 left-1' : '-top-2 -left-2',
          )}
          data-testid="home-remove"
        >
          <Minus size={13} aria-hidden />
        </button>
      )}
    </div>
  )
}

/** The plus in edit mode: every widget at the sizes it draws well, then the apps not on a home screen yet. */
const AddSheet = ({
  open,
  onClose,
  layout,
  page,
  byId,
  onAdd,
}: {
  open: boolean
  onClose: () => void
  layout: PhoneLayout
  page: number
  byId: Map<string, NavItem>
  onAdd: (it: HomeItem) => void
}) => {
  const admin = useAccess((s) => s.admin)
  const lists = sortLists((useAllLists() ?? []).filter((l) => !l.deleted_at && !l.status.converted_to))
  const [listRef, setListRef] = useState<string | undefined>(undefined)
  const apps = [...byId.values()].filter((a) => !onHome(layout, a.key) && !layout.hidden.includes(a.key))
  return (
    <Sheet open={open} onClose={onClose} title={`Add to page ${page + 1}`}>
      <div className="flex flex-col gap-5" data-testid="home-add-sheet">
        <section>
          <h3 className="px-1 pb-2 text-[13px] font-semibold text-muted">Widgets</h3>
          <ul className="group-list">
            {(admin ? adminWidgets() : WIDGET_KINDS).map((kind) => {
              const w = WIDGETS[kind]
              return (
                <li key={kind} className="px-3.5 py-3 flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <w.Icon size={18} className="text-accent shrink-0" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px]">{w.name}</div>
                      <div className="text-xs text-faint">{w.blurb}</div>
                    </div>
                  </div>
                  {kind === 'list' && lists.length > 1 && (
                    <div className="flex flex-wrap gap-1.5 pl-8">
                      {lists.slice(0, 8).map((l) => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => setListRef(l.id)}
                          className={cx(
                            'rounded-full px-2.5 min-h-7 text-xs border',
                            (listRef ?? lists[0]?.id) === l.id
                              ? 'bg-accent-soft border-accent/40 text-fg'
                              : 'bg-raised border-line text-muted',
                          )}
                        >
                          {l.spec.title}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 pl-8">
                    {WIDGET_SIZES[kind].map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() =>
                          onAdd({
                            k: newKey(),
                            t: 'widget',
                            kind,
                            size,
                            ...(kind === 'list' && (listRef ?? lists[0]?.id)
                              ? { ref: listRef ?? lists[0]!.id }
                              : {}),
                          })
                        }
                        className="rounded-full bg-raised border border-line px-3 min-h-8 text-xs inline-flex items-center gap-1"
                        data-testid={`home-add-${kind}-${size}`}
                      >
                        <Plus size={11} aria-hidden /> {SIZE_LABEL[size]}
                      </button>
                    ))}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
        {apps.length > 0 && (
          <section>
            <h3 className="px-1 pb-2 text-[13px] font-semibold text-muted">Apps</h3>
            <div className="grid grid-cols-5 gap-x-1.5 gap-y-3">
              {apps.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => onAdd({ k: newKey(), t: 'app', id: a.key })}
                  className="flex flex-col items-center gap-1.5 min-w-0"
                  data-testid={`home-add-app-${a.key}`}
                >
                  <AppTile app={a} tint={layout.icons} />
                  <AppLabel>{a.label}</AppLabel>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </Sheet>
  )
}

/** Tapping something while arranging (or holding a widget): its size, its page, and taking it off. */
const ItemSheet = ({
  item,
  page,
  layout,
  byId,
  editing,
  onClose,
  onChange,
  onEdit,
}: {
  item: HomeItem | null
  page: number
  layout: PhoneLayout
  byId: Map<string, NavItem>
  editing: boolean
  onClose: () => void
  onChange: (next: PhoneLayout) => void
  onEdit: () => void
}) => {
  const lists = sortLists((useAllLists() ?? []).filter((l) => !l.deleted_at && !l.status.converted_to))
  if (!item) return null
  const title = item.t === 'app' ? (byId.get(item.id)?.label ?? 'App') : WIDGETS[item.kind].name
  const change = (next: PhoneLayout) => {
    onChange(next)
    onClose()
  }
  return (
    <Sheet open={!!item} onClose={onClose} title={title}>
      <div className="flex flex-col gap-4" data-testid="home-item-sheet">
        {item.t === 'widget' && (
          <div>
            <div className="px-1 pb-1.5 text-[13px] font-semibold text-muted">Size</div>
            <div className="flex gap-1.5">
              {WIDGET_SIZES[item.kind].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => change(resizeItem(layout, item.k, s))}
                  className={cx(
                    'rounded-full px-3.5 min-h-9 text-sm border',
                    item.size === s ? 'bg-accent text-on-accent border-transparent' : 'bg-raised border-line',
                  )}
                >
                  {SIZE_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        )}
        {item.t === 'widget' && item.kind === 'list' && lists.length > 1 && (
          <div>
            <div className="px-1 pb-1.5 text-[13px] font-semibold text-muted">Shows</div>
            <div className="flex flex-wrap gap-1.5">
              {lists.slice(0, 12).map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() =>
                    change({
                      ...layout,
                      pages: layout.pages.map((p) =>
                        p.map((x) => (x.k === item.k ? { ...x, ref: l.id } : x)),
                      ),
                    })
                  }
                  className={cx(
                    'rounded-full px-3 min-h-8 text-sm border',
                    item.ref === l.id ? 'bg-accent-soft border-accent/40' : 'bg-raised border-line',
                  )}
                >
                  {l.spec.title}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <div className="px-1 pb-1.5 text-[13px] font-semibold text-muted">Page</div>
          <div className="flex flex-wrap gap-1.5">
            {layout.pages.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => i !== page && change(moveToPage(layout, item.k, i))}
                className={cx(
                  'rounded-full h-9 w-9 text-sm border',
                  i === page ? 'bg-accent text-on-accent border-transparent' : 'bg-raised border-line',
                )}
                aria-label={`Move to page ${i + 1}`}
              >
                {i + 1}
              </button>
            ))}
            {layout.pages.length < MAX_PAGES && (
              <button
                type="button"
                onClick={() => change(moveToPage(layout, item.k, layout.pages.length))}
                className="rounded-full px-3 min-h-9 text-sm border bg-raised border-line inline-flex items-center gap-1"
              >
                <Plus size={13} aria-hidden /> New page
              </button>
            )}
          </div>
        </div>
        <div className="group-list">
          {!editing && (
            <button
              type="button"
              onClick={() => {
                onClose()
                onEdit()
              }}
              className="w-full flex items-center gap-3 px-3.5 min-h-12 text-[15px] text-left hover:bg-raised"
            >
              <Check size={18} className="text-muted" aria-hidden /> Edit Home Screen
            </button>
          )}
          <button
            type="button"
            onClick={() => change(removeItem(layout, item.k))}
            className="w-full flex items-center gap-3 px-3.5 min-h-12 text-[15px] text-left text-rose-300 hover:bg-raised"
            data-testid="home-item-remove"
          >
            <Minus size={18} aria-hidden /> Remove from Home Screen
          </button>
        </div>
      </div>
    </Sheet>
  )
}
